import { Box, Flex } from "@rebass/grid";
import { IFilterCriteria } from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Button, Select, Icon } from "@rentiohq/web-shared/dist/components";
import { Rating } from "components/Rating";
import React from "react";
import styled from "styled-components";
import { RM_COLORS } from "../../rentmatch.colors";

interface IProps {
  filterCriteria: IFilterCriteria;
  onChange: (criteria: IFilterCriteria) => void;
  onSave: () => void;
  onClear: () => void;
  onClose?: () => void;
  isEditing?: boolean;
  activeFilterName?: string;
}

const SectionHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${RM_COLORS.grayMutedAlt};
  text-transform: uppercase;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
`;

const SubLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${RM_COLORS.slate};
  text-transform: uppercase;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const StyledSlider = styled.input<{ percentage: number }>`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  background: linear-gradient(
    to right,
    ${RM_COLORS.borderAlt} ${props => props.percentage}%,
    ${RM_COLORS.borderAlt} ${props => props.percentage}%
  );
  border-radius: 4px;
  outline: none;
  margin-top: 10px;
  margin-bottom: 8px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${RM_COLORS.rentmatchGreen};
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

export const ApplicationsFilterPanel: React.FC<IProps> = ({
  filterCriteria,
  onChange,
  onSave,
  onClear,
  onClose,
  isEditing,
  activeFilterName,
}) => {
  const handleChange = (field: keyof IFilterCriteria, value: any) => {
    onChange({ ...filterCriteria, [field]: value });
  };

  return (
    <Box
      p={4}
      // Negative horizontal margin cancels the parent Card's padding so the
      // panel background spans the full width edge-to-edge (no white gutters).
      mx={-4}
      bg={RM_COLORS.gray50Alt}
      style={{ borderBottom: `1px solid ${RM_COLORS.borderAlt}` }}
    >
      <Flex
        mb={3}
        justifyContent="space-between"
        alignItems="center"
        style={{ height: "30px" }}
      >
        <Box
          fontSize={16}
          color={RM_COLORS.slate}
          style={{
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
            marginRight: "16px",
          }}
        >
          {activeFilterName ||
            getLocalizedText("application.filters.panel.title")}
        </Box>
        {onClose && (
          <Box
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={onClose}
          >
            <Icon source="close" size="medium" />
          </Box>
        )}
      </Flex>

      <Flex mx={-3} flexWrap="wrap" alignItems="flex-start">
        {/* INCOME */}
        <Box width="16%" px={3}>
          <SectionHeader>
            {getLocalizedText("application.filters.panel.income.title")}
          </SectionHeader>
          <SubLabel>
            {getLocalizedText("application.filters.panel.income.minimum")}
          </SubLabel>
          <Select
            id="incomeMultiplier"
            label=""
            value={filterCriteria.incomeMultiplier?.toString() || ""}
            onChange={(option: any) =>
              handleChange(
                "incomeMultiplier",
                option.value ? Number(option.value) : undefined,
              )
            }
            menuPosition="fixed"
            menuPortalTarget={document.body}
            options={[
              { value: "", label: "-" },
              {
                value: "1",
                label: getLocalizedText("application.filters.panel.income.1x"),
              },
              {
                value: "2",
                label: getLocalizedText("application.filters.panel.income.2x"),
              },
              {
                value: "3",
                label: getLocalizedText("application.filters.panel.income.3x"),
              },
              {
                value: "4",
                label: getLocalizedText("application.filters.panel.income.4x"),
              },
            ]}
          />
        </Box>

        {/* RENTMATCH SCORE */}
        <Box width="22%" px={3}>
          <SectionHeader>
            {getLocalizedText("application.filters.panel.rentmatch.title")}
          </SectionHeader>
          <SubLabel>
            {getLocalizedText("application.filters.panel.rentmatch.min")}{" "}
            {filterCriteria.rentMatchMinScore || 0}%
          </SubLabel>
          <Box>
            <StyledSlider
              type="range"
              min="0"
              max="100"
              value={filterCriteria.rentMatchMinScore || 0}
              onChange={e =>
                handleChange("rentMatchMinScore", Number(e.target.value))
              }
              percentage={filterCriteria.rentMatchMinScore || 0}
            />
            <Flex
              justifyContent="space-between"
              fontSize={12}
              style={{ fontWeight: 600 }}
            >
              <Box color={RM_COLORS.slate}>0%</Box>
              <Box color={RM_COLORS.slate}>100%</Box>
            </Flex>
          </Box>
        </Box>

        <Box width="28%" px={3}>
          <SectionHeader>
            {getLocalizedText("application.filters.panel.occupants.title")}
          </SectionHeader>
          <Flex mx={-2}>
            <Box width={1 / 2} px={2}>
              <SubLabel>
                {getLocalizedText("application.filters.panel.occupants.adults")}
              </SubLabel>
              <Select
                id="maxAdults"
                label=""
                value={filterCriteria.maxAdults?.toString() || ""}
                onChange={(option: any) =>
                  handleChange(
                    "maxAdults",
                    option.value ? Number(option.value) : undefined,
                  )
                }
                menuPosition="fixed"
                menuPortalTarget={document.body}
                options={[
                  { value: "", label: "-" },
                  { value: "1", label: "1" },
                  { value: "2", label: "2" },
                  { value: "3", label: "3" },
                  { value: "4", label: "4" },
                  { value: "5", label: "5" },
                ]}
              />
            </Box>
            <Box width={1 / 2} px={2}>
              <SubLabel>
                {getLocalizedText(
                  "application.filters.panel.occupants.children",
                )}
              </SubLabel>
              <Select
                id="maxChildren"
                label=""
                value={filterCriteria.maxChildren?.toString() || ""}
                onChange={(option: any) =>
                  handleChange(
                    "maxChildren",
                    option.value ? Number(option.value) : undefined,
                  )
                }
                menuPosition="fixed"
                menuPortalTarget={document.body}
                options={[
                  { value: "", label: "-" },
                  { value: "0", label: "0" },
                  { value: "1", label: "1" },
                  { value: "2", label: "2" },
                  { value: "3", label: "3" },
                  { value: "4", label: "4" },
                  { value: "5", label: "5" },
                ]}
              />
            </Box>
          </Flex>
        </Box>

        <Box width="20%" px={3}>
          <SectionHeader>
            {getLocalizedText("application.filters.panel.applications.title")}
          </SectionHeader>
          <SubLabel>
            {getLocalizedText("application.filters.panel.applications.label")}
          </SubLabel>
          <Select
            id="minApplications"
            label=""
            value={filterCriteria.minApplications?.toString() || ""}
            onChange={(option: any) =>
              handleChange(
                "minApplications",
                option.value ? Number(option.value) : undefined,
              )
            }
            menuPosition="fixed"
            menuPortalTarget={document.body}
            options={[
              { value: "", label: "-" },
              {
                value: "1",
                label: getLocalizedText(
                  "application.filters.panel.applications.1",
                ),
              },
              {
                value: "2",
                label: getLocalizedText(
                  "application.filters.panel.applications.2",
                ),
              },
              {
                value: "3",
                label: getLocalizedText(
                  "application.filters.panel.applications.3",
                ),
              },
              {
                value: "4",
                label: getLocalizedText(
                  "application.filters.panel.applications.4",
                ),
              },
              {
                value: "5",
                label: getLocalizedText(
                  "application.filters.panel.applications.5",
                ),
              },
            ]}
          />
        </Box>

        {/* OWN RATING */}
        <Box width="14%" px={3}>
          <SectionHeader>
            {getLocalizedText("application.filters.panel.rating.title")}
          </SectionHeader>
          <SubLabel>
            {getLocalizedText("application.filters.panel.rating.minimum")}
          </SubLabel>
          <Box>
            <Rating
              value={filterCriteria.minRating || 0}
              onChange={(val: number) => handleChange("minRating", val)}
              size={24}
            />
          </Box>
        </Box>
      </Flex>

      <Flex justifyContent="flex-end" mt={4} alignItems="center">
        <Box
          mr={3}
          style={{
            cursor: "pointer",
            color: RM_COLORS.rentmatchGreen,
            fontSize: 14,
            fontWeight: 600,
          }}
          onClick={onClear}
        >
          {getLocalizedText("application.filters.panel.clear")}
        </Box>
        <Button appearance="primary" onClick={onSave}>
          {isEditing
            ? getLocalizedText("application.filters.panel.update")
            : getLocalizedText("application.filters.panel.save")}
        </Button>
      </Flex>
    </Box>
  );
};
